/** Pixel rectangle react-easy-crop reports, in the source image's own scale. */
export type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Logos render inside circles no bigger than ~72px, so 512² is already
 * generous for retina — anything larger is bytes the merchant uploads and
 * every customer downloads for nothing.
 */
const OUTPUT_SIZE = 512;

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () =>
      reject(new Error("Could not read that image"))
    );
    image.src = src;
  });

/**
 * Renders the selected crop to a square PNG. PNG rather than JPEG because
 * logos are the one image type that genuinely needs transparency — a brand
 * mark flattened onto white looks broken on any tinted surface.
 *
 * The circular mask stays a presentation concern: the file is square, and
 * every surface that shows it (PageAvatar, the switcher) already clips to a
 * circle. That keeps the asset usable if a square slot ever needs it.
 */
export const cropImageToFile = async (
  imageSrc: string,
  area: CropArea,
  fileName: string
): Promise<File> => {
  const image = await loadImage(imageSrc);

  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process that image");

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png")
  );
  if (!blob) throw new Error("Could not process that image");

  const baseName = fileName.replace(/\.[^.]+$/, "") || "logo";
  return new File([blob], `${baseName}.png`, { type: "image/png" });
};
